import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { Box, FormControl, InputLabel, Select, SelectChangeEvent, TextField, MenuItem, Grid, FormHelperText, List, ListItem, ListItemIcon, ListItemText, Checkbox, FormControlLabel, Switch, Alert } from '@mui/material';
import { Franchise, Persona, Store, User } from '../../../../app/services/api/generated';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import LoadingWheel from '../../../../components/ui/LoadingWheel';
import { isEmailValid } from '../../../../utils';
import useLogError from '../../../../hooks/useLogError';
import { UpsertUserRequest } from '../types/UpsertUserRequest';
import LeavePageModal from '../../../../components/shared/dialogs/LeavePageModal';
import { UserContext } from '../../../../components/shared/useUser';
interface SubmitModalProps {
  openModal: boolean;
  onConfirm: (request: UpsertUserRequest) => void;
  editUser: User | undefined;
  onCancel: () => void;
  onResetPassword: () => void;
}

const CreateUpdateUserModal: React.FC<SubmitModalProps> = ({ openModal, onConfirm, editUser, onCancel, onResetPassword }) => {
  const { t } = useTranslation('manageUsers');
  const { getPersonas } = useNswagClient();
  const { user } = useContext(UserContext);
  const [accessibleStores, setAccessibleStores] = useState<Store[]>([]);
  const [accessibleFranchises, setAccessibleFranchises] = useState<Franchise[]>([]);

  useEffect(() => {
    if (user) {
      setAccessibleStores(user?.stores || []);
      setAccessibleFranchises(user?.franchises || []);
      setFranchiseList(user?.franchises || []);
    }
  }, [user]);

  const [request, setRequest] = useState<UpsertUserRequest>({
    emailAddress: '',
    franchiseName: '',
    personaId: undefined,
    useNewUI: true,
    stores: [],
    franchiseIds: [],
    allStores: false,
  });
  const [initialRequest, setInitialRequest] = useState<UpsertUserRequest | undefined>(undefined);
  const [errors, setErrors] = useState<Partial<Record<keyof UpsertUserRequest, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof UpsertUserRequest, boolean>>>({});
  const [franchiseList, setFranchiseList] = useState<Franchise[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const { logError } = useLogError();

  useEffect(() => {
    if (openModal) {
      getData();
    }
  }, [editUser, openModal]);

  const getData = () => {
    setIsLoading(true);
    const newRequest: UpsertUserRequest = {
      emailAddress: editUser?.emailAddress ?? '',
      personaId: editUser?.persona?.id,
      franchiseName: editUser?.franchiseName ?? '',
      useNewUI: true,
      stores: editUser?.stores?.filter(s => s.storeNumber !== undefined).map(s => s.storeNumber ?? '') ?? [],
      franchiseIds: editUser?.franchises?.filter(f => f.franchiseId !== undefined).map(f => f.franchiseId ?? -1) ?? [],
      allStores: editUser?.allStores ?? false,
    };

    setRequest(newRequest);
    setInitialRequest(newRequest);
    setErrors({});
    setTouched({});
    getPersonaData();
    setIsLoading(false);
  };

  const getPersonaData = async () => {
    try {
      const result = await getPersonas();
      if (result?.data) {
        setPersonas(result.data);
      }
    } catch (error) {
      logError(error);
    } finally {
      setIsLoading(false);
    }
  };


  const isFormDirty = () => {
    if (!request || !initialRequest) return false;
    return JSON.stringify(request) !== JSON.stringify(initialRequest);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const validateField = (field: keyof UpsertUserRequest, value: any): string | undefined => {
    if (field === 'emailAddress' && value) {
      return isEmailValid(value) ? undefined : t('invalidEmail');
    }
    if (['name', 'emailAddress', 'franchiseName', 'personaId'].includes(field) && !value) {
      return t(`required${field}`);
    }
    return undefined;
  };

  const handleChange = (field: keyof UpsertUserRequest) => (
    event: React.ChangeEvent<HTMLInputElement | { value: unknown }> | SelectChangeEvent<string | number>,
  ) => {
    const value = event.target.value;
    setRequest((prev) => ({ ...prev, [field]: value }));

    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleBlur = (field: keyof UpsertUserRequest) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, request[field]);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleCancel = () => {
    if (isFormDirty()) {
      setShowLeaveModal(true);
    } else {
      onCancel();
      setRequest({
        emailAddress: '',
        franchiseName: '',
        personaId: undefined,
        useNewUI: true,
        stores: [],
        franchiseIds: [],
        allStores: false,
      });
    }
  };

  const handleConfirm = () => {
    const emailValid = isEmailValid(request.emailAddress);
    if (!emailValid) {
      setErrors((prev) => ({ ...prev, emailAddress: t('invalidEmail') }));
      setTouched((prev) => ({ ...prev, emailAddress: true }));
      return;
    }

    const newErrors: Partial<Record<keyof UpsertUserRequest, string>> = {};

    (['emailAddress', 'franchiseName', 'personaId'] as (keyof UpsertUserRequest)[]).forEach((field) => {
      const error = validateField(field, request[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched({
        emailAddress: true,
        franchiseName: true,
        personaId: true,
      });
      return;
    }

    onConfirm(request);
    setRequest({
      emailAddress: '',
      franchiseName: '',
      personaId: undefined,
      useNewUI: true,
      stores: [],
      franchiseIds: [],
      allStores: false,
    });
    onCancel();
  };

  const isValid = (request.franchiseName || request.allStores) && request.personaId && isEmailValid(request.emailAddress);

  if (isLoading) return <LoadingWheel />;

  return (
    <>
      <Dialog
        open={openModal}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
        aria-labelledby="customized-dialog-title"
        aria-describedby="customized-dialog-description"
        sx={{
          '& .MuiDialog-container': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="displayMD">{t('manageUser')}</Typography>
            <Typography variant="textMD"
              color="text.secondary">
              {t('editExistingUser')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <IconButton
              aria-label="close"
              onClick={handleCancel}
              sx={{ color: (theme) => theme.palette.custom.gray[700] }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 7, pb: 4 }}>
          <Grid container
            spacing={4}
            sx={{ maxWidth: '100%' }}
            pt={5}>
            <Grid item
              xs={12}
              md={5}>
              <FormControl fullWidth
                error={touched.emailAddress && !!errors.emailAddress}>
                <TextField
                  required
                  fullWidth
                  size="small"
                  label={t('email')}
                  name="email"
                  value={request.emailAddress}
                  onChange={handleChange('emailAddress')}
                  onBlur={handleBlur('emailAddress')}
                  error={touched.emailAddress && !!errors.emailAddress}
                />
                <FormHelperText sx={{ minHeight: '20px', mt: '6px' }}>
                  {touched.emailAddress && errors.emailAddress ? errors.emailAddress : ' '}
                </FormHelperText>
              </FormControl>
              <FormControl
                required
                variant="outlined"
                size="small"
                fullWidth
                error={touched.franchiseName && !!errors.franchiseName}
              >
                <InputLabel id="franchise-select-label">{t('company')}</InputLabel>
                <Select
                  labelId="franchise-select-label"
                  label={t('company')}
                  name="franchiseName"
                  value={request.franchiseName}
                  onChange={handleChange('franchiseName')}
                  onBlur={handleBlur('franchiseName')}
                >
                  {franchiseList.map((franchise) => (
                    <MenuItem key={franchise.franchiseName}
                      value={franchise.franchiseName}>
                      {franchise.franchiseName}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText sx={{ minHeight: '20px', mt: '6px' }}>
                  {touched.franchiseName && errors.franchiseName ? errors.franchiseName : ' '}
                </FormHelperText>
              </FormControl>
              <FormControl
                required
                variant="outlined"
                size="small"
                fullWidth
                error={touched.personaId && !!errors.personaId}
              >
                <InputLabel id="user-role-select-label">{t('role')}</InputLabel>
                <Select
                  labelId="user-role-select-label"
                  label={t('role')}
                  name="personaId"
                  value={request.personaId ?? ''}
                  onChange={handleChange('personaId')}
                  onBlur={handleBlur('personaId')}
                >
                  {personas.map((p) => (
                    <MenuItem key={p.id}
                      value={p.id}>
                      {p.description}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText sx={{ minHeight: '20px', mt: '6px' }}>
                  {touched.personaId && errors.personaId ? errors.personaId : ' '}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
          {(user?.franchises?.length ?? 0) > 0 && (
            <FormControlLabel
              control={<Switch checked={request.allStores}
                onChange={(_, checked) => setRequest({ ...request, allStores: checked })} />}
              label={request.allStores ? t('assignFranchises') : t('assignStores')}
              sx={{ mb: 2 }}
            />)}
          {request.allStores ? (
            <>
              <Alert severity="info"
                sx={{ mb: 2 }}>
                Linking a franchise will grant access to <b>all stores</b> within that franchise.
              </Alert>
              <List sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ccc', borderRadius: 1 }}>
                {accessibleFranchises.filter(f => f.franchiseId !== undefined).map(franchise => (
                  <ListItem
                    key={franchise.franchiseId}
                    onClick={() => {
                      let selected = request.franchiseIds || [];

                      if (selected.includes(franchise.franchiseId ?? -1)) {
                        selected = selected.filter(id => id !== franchise.franchiseId);
                      } else {
                        selected = [...selected, franchise.franchiseId ?? -1];
                      }

                      setRequest(prev => ({
                        ...prev,
                        franchiseIds: selected,
                      }));
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={request.franchiseIds?.includes(franchise.franchiseId ?? -1) || false}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText primary={franchise.franchiseName} />
                  </ListItem>
                ))}
              </List>
              <FormHelperText sx={{ minHeight: '20px', mt: '6px' }}>
                {touched.franchiseIds && errors.franchiseIds ? errors.franchiseIds : ' '}
              </FormHelperText>
            </>
          ) : (
            <>
              <List sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ccc', borderRadius: 1 }}>
                {accessibleStores
                  .filter(store => {
                    const franchise = franchiseList.find(f => f.franchiseName === request.franchiseName);
                    if (!franchise) return false;
                    return store.franchiseId === franchise.franchiseId ||
                      store.franchiseName?.toLocaleLowerCase() === request.franchiseName.toLocaleLowerCase();
                  })
                  .map(store => (
                    <ListItem
                      key={store.storeNumber}
                      onClick={() => {
                        let selected = request.stores || [];

                        if (store.storeNumber === undefined) return;

                        if (selected.includes(store.storeNumber)) {
                          selected = selected.filter(s => s !== store.storeNumber);
                        } else {
                          selected = [...selected, store.storeNumber];
                        }

                        setRequest(prev => ({
                          ...prev,
                          stores: selected,
                        }));
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={request.stores?.includes(store.storeNumber ?? '-1') || false}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={store.storeName} />
                    </ListItem>
                  ))}
              </List>
              <FormHelperText sx={{ minHeight: '20px', mt: '6px' }}>
                {touched.stores && errors.stores ? errors.stores : (!request.franchiseName ? t('selectCompany') : ' ')}
              </FormHelperText>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ pr: 7, pb: 7, pt: 2, justifyContent: 'flex-end' }}>
          <Button variant="secondary"
            size="lg"
            onClick={handleCancel}
            sx={{ borderRadius: '8px', px: 3 }}>
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            disabled={!isValid}
            onClick={handleConfirm}
            sx={{ borderRadius: '8px', px: 3 }}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog >
      <LeavePageModal
        open={showLeaveModal}
        onConfirmUrl=""
        onClose={() => setShowLeaveModal(false)}
        onConfirm={() => {
          handleConfirm();
          setShowLeaveModal(false);
        }}
        onCancel={() => {
          setShowLeaveModal(false);
          onCancel();
          setRequest({
            emailAddress: '',
            franchiseName: '',
            personaId: undefined,
            useNewUI: true,
            stores: [],
            franchiseIds: [],
            allStores: false,
          });
        }}
      />
    </>
  );
};

export default CreateUpdateUserModal;
